import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { installerApi, uploadViaSignedUrl } from '../../utils/installerApi';
import { reverseGeocode } from '../../utils/reverseGeocode';
import { useInstallerSession } from '../../hooks/useInstallerSession';
import MapAddressPicker from './MapAddressPicker';
import { InstallerTopbar } from './InstallerChrome';
import InstallBanner from './InstallBanner';
import '../../styles/InstallerPages.css';

const DAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه']; // index 0..6

// Convert Latin (0-9) AND Arabic-Indic (٠-٩) digits to Persian (۰-۹). Used for
// the work-hours labels and to localise digits the installer types into the
// address field, so numbers read natively in the Persian UI. (Work-hours values
// keep their own Latin "HH:MM" source for the backend; only the label is mapped.)
const toFa = (s) => String(s)
  .replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
  .replace(/[٠-٩]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d.charCodeAt(0) - 0x0660]);

// Work-hours options every 30 minutes. value = Latin "HH:MM" (backend format),
// label = Persian digits (display).
const TIME_OPTS = [];
for (let h = 0; h < 24; h += 1) {
  for (const m of [0, 30]) {
    TIME_OPTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

// Map raw fetch/network failures to a Persian message so the installer never
// sees a bare English "Failed to fetch" / "Load failed". Server-sent errors
// (already Persian) pass through unchanged.
function faError(e) {
  const m = (e && e.message) || String(e);
  if (/failed to fetch|load failed|networkerror|err_network|connection/i.test(m)) {
    return 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.';
  }
  return m;
}

// Unified phone-first auth for installers (the Snapp / Tapsi / Uber pattern):
// ONE phone + ONE OTP, then the system branches automatically — an existing
// installer is logged in and routed by status, a new installer continues to the
// profile form WITHOUT being asked for phone/OTP again. Replaces the old split
// InstallerLoginPage + InstallerSignupPage, which asked for phone+code twice.
export default function InstallerAuthPage() {
  const nav = useNavigate();
  const { setToken } = useInstallerSession();
  const [stage, setStage] = useState('phone'); // 'phone' | 'otp' | 'profile'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [signupTicket, setSignupTicket] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: '', base_address_text: '', loc: null, vehicle_type: 'motorcycle',
    work_days: [0, 1, 2, 3, 4, 5, 6], work_start: '08:00', work_end: '18:00',
  });
  const [files, setFiles] = useState({ resume: null, national_id: null, profile_photo: null });
  const [addrLoading, setAddrLoading] = useState(false);
  const revSeq = useRef(0);

  // When the installer drops/moves the base-location pin, reverse-geocode it
  // (debounced) and fill the address field. The field stays editable so they can
  // append پلاک/واحد; the next pin move overwrites. revSeq discards stale responses.
  const locLat = form.loc?.lat ?? null;
  const locLng = form.loc?.lng ?? null;
  useEffect(() => {
    if (locLat == null || locLng == null) return undefined;
    const seq = ++revSeq.current;
    const timer = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const addr = await reverseGeocode(locLat, locLng);
        if (seq === revSeq.current && addr) setForm((f) => ({ ...f, base_address_text: toFa(addr) }));
      } catch { /* leave the field editable for manual entry on failure */ }
      finally { if (seq === revSeq.current) setAddrLoading(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [locLat, locLng]);

  const guard = (fn) => async () => {
    setErr(null); setBusy(true);
    try { await fn(); } catch (e) { setErr(faError(e)); } finally { setBusy(false); }
  };

  const sendOtp = guard(async () => {
    if (!phone.trim()) throw new Error('شماره موبایل را وارد کنید.');
    await installerApi.requestOtp(phone);
    setSignupTicket(null);
    setStage('otp');
  });

  const verify = guard(async () => {
    const r = await installerApi.verifyOtp(phone, otp);
    if (r.exists) {
      // Existing installer — log in and route by approval status.
      setToken(r.session_token);
      nav(r.status === 'approved' ? '/installer/dashboard' : '/installer/pending');
    } else {
      // New installer — phone is already verified, so go straight to the
      // profile form. Keep its short-lived proof in memory only.
      setSignupTicket(r.signup_ticket);
      setStage('profile');
    }
  });

  const submitProfile = guard(async () => {
    if (!form.full_name || !form.loc) throw new Error('نام و موقعیت روی نقشه الزامی است.');
    if (!files.resume || !files.national_id || !files.profile_photo) throw new Error('رزومه، کارت ملی و عکس پروفایل الزامی است.');
    if (!signupTicket) {
      setStage('phone');
      throw new Error('مجوز ثبت‌نام منقضی شده است. لطفاً دوباره کد تایید دریافت کنید.');
    }
    const fmt = (f) => (f.type === 'application/pdf' ? 'pdf' : f.name.toLowerCase().endsWith('.docx') ? 'docx' : 'image');
    const credential = { signup_ticket: signupTicket };
    const resume_path = await uploadViaSignedUrl('resume', files.resume, credential, fmt(files.resume));
    const national_id_path = await uploadViaSignedUrl('national_id', files.national_id, credential, 'image');
    const profile_photo_path = await uploadViaSignedUrl('profile_photo', files.profile_photo, credential, 'image');
    let r;
    try {
      r = await installerApi.signup({
        signup_ticket: signupTicket, phone, full_name: form.full_name,
        base_address_text: form.base_address_text, base_lat: form.loc.lat, base_lng: form.loc.lng,
        resume_path, resume_format: fmt(files.resume), national_id_path, profile_photo_path,
        vehicle_type: form.vehicle_type, work_hours_days: form.work_days,
        work_hours_start: form.work_start, work_hours_end: form.work_end,
      });
    } catch (e) {
      setSignupTicket(null); setOtp(''); setStage('phone');
      const message = faError(e);
      throw new Error(/کد تایید جدید/.test(message) ? message : `${message} برای تلاش دوباره، کد تایید جدید دریافت کنید.`);
    }
    setToken(r.session_token);
    nav('/installer/pending');
  });

  const toggleDay = (i) => setForm((f) => ({
    ...f, work_days: f.work_days.includes(i) ? f.work_days.filter((x) => x !== i) : [...f.work_days, i],
  }));

  return (
    <div className="inst">
      <div className="inst-shell">
        <InstallerTopbar sub={stage === 'profile' ? 'ثبت‌نام نیروی اجرایی فیبر نوری' : 'ورود نیروی اجرایی'} />
        <InstallBanner />

        <div className="inst-card">
          {err && <div role="alert" className="inst-alert">{err}</div>}

          {stage === 'phone' && (
            <>
              <h1 className="inst-h1">ورود نیروی اجرایی</h1>
              <p className="inst-lead">شماره موبایل خود را وارد کنید. اگر حساب نداشته باشید، در همین مسیر ثبت‌نام می‌شوید.</p>
              <div className="inst-field">
                <label className="inst-label" htmlFor="phone">شماره موبایل</label>
                <input className="inst-input" id="phone" inputMode="numeric" value={phone}
                  onChange={(e) => setPhone(e.target.value)} placeholder="09xxxxxxxxx" />
              </div>
              <button className="inst-btn" disabled={busy} onClick={sendOtp}>
                {busy && <span className="inst-spin" />}ادامه
              </button>
            </>
          )}

          {stage === 'otp' && (
            <>
              <h1 className="inst-h1">کد تایید</h1>
              <p className="inst-lead">کد ارسال‌شده به شماره {phone} را وارد کنید.</p>
              <div className="inst-field">
                <label className="inst-label" htmlFor="otp">کد تایید</label>
                <input className="inst-input inst-otp" id="otp" inputMode="numeric" maxLength={4} value={otp}
                  onChange={(e) => setOtp(e.target.value)} placeholder="----" />
              </div>
              <div className="inst-btnrow">
                <button className="inst-btn inst-btn-ghost" disabled={busy} onClick={() => { setOtp(''); setStage('phone'); }}>بازگشت</button>
                <button className="inst-btn" disabled={busy} onClick={verify}>
                  {busy && <span className="inst-spin" />}تایید
                </button>
              </div>
            </>
          )}

          {stage === 'profile' && (
            <>
              <h1 className="inst-h1">تکمیل ثبت‌نام</h1>
              <p className="inst-lead">شماره شما تایید شد. اطلاعات زیر برای تایید و اعزام شما استفاده می‌شود.</p>

              <div className="inst-field">
                <label className="inst-label" htmlFor="name">نام و نام خانوادگی</label>
                <input className="inst-input" id="name" value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="مثلاً علی رضایی" />
              </div>

              <div className="inst-field">
                <label className="inst-label" htmlFor="addr">آدرس خانه/پایگاه</label>
                <input className="inst-input" id="addr" value={form.base_address_text}
                  onChange={(e) => setForm({ ...form, base_address_text: toFa(e.target.value) })} placeholder="آدرس متنی" />
                <div className="inst-map"><MapAddressPicker value={form.loc} onChange={(loc) => setForm({ ...form, loc })} height={220} /></div>
                <div className="inst-map-hint">{addrLoading ? 'در حال یافتن آدرس...' : 'موقعیت پایگاه خود را روی نقشه لمس کنید (برای اعزام نزدیک‌ترین کار).'}</div>
              </div>

              <div className="inst-field">
                <label className="inst-label" htmlFor="vehicle">نوع وسیله نقلیه</label>
                <select className="inst-select" id="vehicle" value={form.vehicle_type}
                  onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}>
                  <option value="car">ماشین</option>
                  <option value="motorcycle">موتور</option>
                  <option value="foot">پیاده</option>
                </select>
              </div>

              <fieldset className="inst-fieldset">
                <legend className="inst-legend">ساعات کاری</legend>
                <div className="inst-daypills">
                  {DAYS.map((d, i) => (
                    <button type="button" key={d} aria-pressed={form.work_days.includes(i)}
                      className={`inst-daypill ${form.work_days.includes(i) ? 'is-on' : ''}`}
                      onClick={() => toggleDay(i)}>{d}</button>
                  ))}
                </div>
                <div className="inst-timerow">
                  <select className="inst-select" aria-label="ساعت شروع" style={{ flex: 1 }} value={form.work_start}
                    onChange={(e) => setForm({ ...form, work_start: e.target.value })}>
                    {TIME_OPTS.map((t) => <option key={t} value={t}>{toFa(t)}</option>)}
                  </select>
                  <span className="inst-timesep">تا</span>
                  <select className="inst-select" aria-label="ساعت پایان" style={{ flex: 1 }} value={form.work_end}
                    onChange={(e) => setForm({ ...form, work_end: e.target.value })}>
                    {TIME_OPTS.map((t) => <option key={t} value={t}>{toFa(t)}</option>)}
                  </select>
                </div>
              </fieldset>

              <div className="inst-field">
                <label className="inst-label" htmlFor="resume">رزومه (PDF/DOCX/تصویر)</label>
                <input className="inst-file" id="resume" type="file" accept=".pdf,.docx,image/*"
                  onChange={(e) => setFiles({ ...files, resume: e.target.files[0] })} />
              </div>
              <div className="inst-field">
                <label className="inst-label" htmlFor="nid">تصویر کارت ملی</label>
                <input className="inst-file" id="nid" type="file" accept="image/*"
                  onChange={(e) => setFiles({ ...files, national_id: e.target.files[0] })} />
              </div>
              <div className="inst-field">
                <label className="inst-label" htmlFor="photo">عکس پروفایل</label>
                <input className="inst-file" id="photo" type="file" accept="image/*"
                  onChange={(e) => setFiles({ ...files, profile_photo: e.target.files[0] })} />
              </div>

              <button className="inst-btn" disabled={busy} onClick={submitProfile}>
                {busy && <span className="inst-spin" />}ثبت‌نام
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
